<template>
    <div class="products">
        <h3 class="mb-3">Products</h3>

        <div v-if="productsLoaded">

            <div class="mb-3">
                <button type="button" class="btn btn-success" @click="addProduct">
                    Add Product
                </button>
            </div>

            <table v-if="productsLoaded && products && products.length" class="table table-bordered table-striped products-table mb-0">
                <thead class="bg-white">
                    <th>Name</th>
                    <th>SKU</th>
                    <th>Price</th>
                    <th>Actions</th>
                </thead>
                <tbody>
                    <tr v-for="(product, index) in products" :key="index" :class="{'deleting': product.deleting}">
                        <td>{{product.name}}</td>
                        <td>{{product.sku}}</td>
                        <td>{{product.price}}</td>
                        <td width="1px">
                            <div class="d-flex flex-nowrap">
                                <button type="button" class="btn btn-primary mr-1" @click.prevent="editProduct(product)" :disabled="product.deleting">
                                    <font-awesome-icon :icon="['far', 'edit']" fixed-width />
                                </button>
                                <button type="button" class="btn btn-danger" @click.prevent="deleteProduct(product, index)" :disabled="product.deleting">
                                    <div v-if="product.deleting">
                                        <font-awesome-icon :icon="['fad', 'circle-notch']" fixed-width spin />
                                    </div>
                                    <div v-else>
                                        <font-awesome-icon :icon="['far', 'trash']" fixed-width />
                                    </div>
                                </button>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="productsLoaded && (!products || products && !products.length)" class="text-danger">
                0 products
            </div>
        </div>
        <div v-else>
            <!-- <font-awesome-icon :icon="['fad', 'circle-notch']" spin /> -->
            <content-placeholders>
                <content-placeholders-text :lines="5" />
            </content-placeholders>
        </div>

        <b-modal id="product-modal" size="lg">
            <template v-slot:modal-header>
                <h5 class="modal-title align-self-center">
                    <div v-if="editableProduct.id">Edit: <strong>{{editableProductCache.name}}</strong></div>
                    <div v-else>Add Product</div>
                </h5>
                <div class="align-self-center">
                    <button type="button" class="close" aria-label="Close" @click="closeProductModal">
                        <font-awesome-icon :icon="['fas', 'times']" />
                    </button>
                </div>
            </template>
            <template v-slot:default>

                <div class="form-group">
                    <label for="name">Name</label>
                    <input ref="productName" type="text" class="form-control" :class="{'is-invalid': underValidation && !editableProduct.name}" v-model="editableProduct.name">
                    <div class="invalid-feedback">
                        Name is required
                    </div>
                </div>
                <div class="form-group">
                    <label for="sku">SKU</label>
                    <input ref="productSKU" type="text" class="form-control" :class="{'is-invalid': underValidation && !editableProduct.sku || skuExists}" v-model.trim="editableProduct.sku">
                    <div class="invalid-feedback">
                        <div v-if="underValidation && !editableProduct.sku">SKU is required</div>
                        <div v-if="skuExists">A product with that SKU already exists</div>
                    </div>
                </div>

                <div class="form-group">
                    <label for="legacyId">Legacy ID</label>
                    <input ref="productLegacyId" type="number" class="form-control" :class="{'is-invalid': underValidation && !editableProduct.legacy_id}" v-model.number="editableProduct.legacy_id" :step="'any'">
                    <div class="invalid-feedback">
                        Legacy ID is required
                    </div>
                </div>

                <div class="form-group">
                    <label for="price">Price</label>
                    <input ref="productPrice" type="number" class="form-control" :class="{'is-invalid': underValidation && !editableProduct.price}" v-model.number="editableProduct.price" :step="'any'">
                    <div class="invalid-feedback">
                        Price is required
                    </div>
                </div>

                <div class="form-group">
                    <label for="price">Thumbnail</label>
                    <input ref="productThumbnail" type="text" class="form-control" v-model="editableProduct.thumbnail">
                </div>

                <div class="form-group">
                    <label for="price">Product Group</label>
                    <input ref="product_group" type="text" class="form-control" v-model="editableProduct.product_group">
                </div>

                <div class="form-group">
                    <label for="price">Product Group Handle</label>
                    <input ref="product_group_handle" type="text" class="form-control" v-model="editableProduct.product_group_handle">
                </div>

                <div class="form-group">
                    <label for="price">Product Group Order</label>
                    <input ref="product_group_order" type="text" class="form-control" v-model="editableProduct.product_group_order">
                </div>

                <div class="form-group">
                    <label for="price">Product Info HTML</label>
                    <textarea ref="page_html" rows=15 type="text" class="form-control" style='font-family: monospace' v-model="editableProduct.page_html"></textarea>
                </div>

                <div class="form-group mb-0">
                    <label for="states">States &amp; Grades</label>
                    <div class="table-responsive mb-0">
                        <table class='table table-sm table-bordered product-state-grade-table mb-0'>
                            <thead>
                            <tr>
                                <th></th>
                                <th v-for="(grade, index) in grades" :key="index" scope="col" @click="bulkRelateGrade(editableProduct, states, grade)">
                                    <template v-if="grade == 0">K</template>
                                    <template v-else>{{grade}}</template>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(state, stateIndex) in states" :key="state.state">
                                <th @click="bulkRelateState(editableProduct, state, grades)">{{state.fullState}}</th>
                                <td v-for="(grade, gradeIndex) in grades" class='grid text-center' @click="relate(editableProduct, state, grade)" :key="gradeIndex">
                                    <font-awesome-icon v-if="isRelated(editableProduct.statesgrades, state, grade)" :icon="['far', 'check']" class="text-success" fixed-width />
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
            <template v-slot:modal-footer>
                <button type="button" class="btn btn-light" @click="closeProductModal" :disabled="savingProduct">Cancel</button>
                <button type="button" class="btn" :class="{'btn-primary': editableProduct.id, 'btn-success': !editableProduct.id}" :disabled="savingProduct" @click.prevent="saveProduct">
                    <div v-if="savingProduct">
                        <font-awesome-icon :icon="['fad', 'circle-notch']" spin />
                    </div>
                    <div v-else>
                        <div v-if="editableProduct.id">Save Changes</div>
                        <div v-else>Add Product</div>
                    </div>
                </button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped>
    .table th,
    .table td {
        vertical-align: middle;
    }

    .table tr.deleting {
        opacity: .5;
    }

    .product-state-grade-table > thead > tr > th {
        min-width: 45px;
        text-align: center;
    }

    .product-state-grade-table > thead > tr > th:not(:first-child),
    .product-state-grade-table > tbody > tr > td,
    .product-state-grade-table > tbody > tr > th {
        cursor: pointer;
        user-select: none;
    }

    .unit-control {
        padding: 15px;
        margin: -15px;
        cursor: pointer;
    }

    .test-standards.active {
        color: #333;
        background-color: #e6e6e6;
        -webkit-box-shadow: inset 0 3px 5px rgba(0, 0, 0, .125);
        box-shadow: inset 0 3px 5px rgba(0, 0, 0, .125);
    }

    .unit-standards .nav-pills {
        margin-bottom: 5px;
    }

    #standards .unit-standards-label {
        width: 100%;
    }

    .unit-standards .nav-pills .nav-item {
        width: 139px;
        margin-bottom: 5px;
    }

    .unit-standards .nav-pills .nav-item {
        margin-right: 5px;
    }

    .unit-standards .nav-pills .nav-link {
        padding: 3px 10px;
        background: #A7A9AC;
        color: white;
        text-align: center;
    }

    .unit-standards .nav-pills > li.active > a, .unit-standards .nav-pills > li.active > a:focus, .unit-standards .nav-pills > li.active > a:hover {
        background-color: #0262A0;
    }

    .nav-pills > li + li {
        margin-left: 0;
    }
</style>

<script>
import { mapState } from 'vuex'





import states from '@/common/states'

export default {
    data: () => ({
        products: [],

        // grades: [0, 1, 2, 3, 4, 5, 6, 7, 8],
        grades: ['0', '1', '2', '3', '4', '5', '6', '7', '8'],
        gradesByName: ["k", "one", "two", "three", "four", "five", "six", "seven", "eight"],

        states: states,

        editableProduct: {
            statesgrades: []
        },
        editableProductCache: {
            statesgrades: []
        },

        skuExists: false,
        productsLoaded: false,
        savingProduct: false,
        underValidation: false
    }),
    computed: mapState({
        stateData: state => state.stateData.stateData,
        selectedState: state => state.stateData.selectedState,
        lowerCaseState () {
            return this.state.toLowerCase().trim()
        },
        formattedState () {
            let formattedState = null

            _.forEach(this.states, state => {
                if (this.state == state.state) {
                    formattedState = state.fullState
                }
            })

            return formattedState
        },
        skus () {
            let skus = []

            _.forEach(this.products, product => {
                skus.push(product.sku)
            })

            return skus
        }
    }),
    watch: {
		'editableProduct.sku'(sku) {
            // when editing a product SKU, check if SKU matches any other existing product SKUS
            if (
                sku !== this.editableProductCache.sku &&
                _.includes(this.skus, sku)
            ) {
                 this.skuExists = true
            } else {
                 this.skuExists = false
            }
		}
	},
    methods: {
        getProducts () {
            this.productsLoaded = false

            this.$http({
                'url': `dashboard/product/list`,
                'method': 'get'
            }).then((response) => {
                if (response.data.success) {
                    this.products = _.orderBy(response.data.products, 'created_at')

                    this.productsLoaded = true
                } else {
                    this.$swal({
                        text: response.data.message,
                        type: 'warning'
                    })
                }
            })
        },
        addProduct () {
            this.clearEditableProduct()
            this.$bvModal.show('product-modal')
        },
        editProduct (product) {
            /*
                clone passed product to two separate objects so that they can be compared against each other
            */
            this.editableProduct = _.cloneDeep(product)
            this.editableProductCache = _.cloneDeep(product)

            this.$bvModal.show('product-modal')
        },
        deleteProduct (product, index) {
            this.$swal({
                html: "Are you sure you want to delete this product?",
                showCancelButton: true,
                confirmButtonText: 'Delete Unit',
                confirmButtonClass: 'btn btn-danger',
                cancelButtonText: 'Cancel',
                cancelButtonClass: 'btn btn-light',
                buttonsStyling: false,
                reverseButtons: true
            }).then((willDelete) => {
                if (willDelete.value) {
                    // add 'deleting' prop for visual feedback
                    this.$set(product, 'deleting', true)

                    this.$http({
                        'url': 'dashboard/product/delete/' + product.id,
                        'method': 'delete'
                    }).then((response) => {
                        if (response.data.success) {
                            // remove product from products array
                            this.products.splice(index, 1)
                        } else {
                            this.$swal({
                                text: response.data.message,
                                type: 'warning'
                            })
                        }
                    })
                }
            })
        },
        saveProduct () {
            // if product does not pass validation, do not continue saving
            if (!this.validateProduct()) { return false }

            if (!this.productChanges()) {
                // when editing a product, if no changes were made, close modal without making a server call
                this.$bvModal.hide('product-modal')

                this.clearEditableProduct()
            } else {
                this.savingProduct = true

                this.$http({
                    'url': `dashboard/product/save`,
                    'method': 'post',
                    'data': this.editableProduct
                }).then((response) => {
                    if (response.data.success) {
                        let message = null

                        // finds the product in the 'products' array so we know whether to save to an already existing item or create a new one
                        let product = _.find(this.products, {
                            id: this.editableProduct.id
                        })

                        if (product) {

                            // if product already exists, replace it
                            let index = _.findIndex(this.products, product)

                            this.products.splice(index, 1, this.editableProduct)

                            message = "Product saved"
                        } else {
                            /*
                                the endpoint should respond with an ID. I add the ID to the object when created so that I do not need to reload the data
                                and the item can be edited right away
                            */
                            this.$set(this.editableProduct, 'id', response.data.id)

                            this.products.push(this.editableProduct)

                            message = "Product created"
                        }

                        this.$bvModal.hide('product-modal')

                        this.$swal({
                            toast: true,
                            html: message,
                            type: 'success',
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 5000
                        })

                        // clear the object used while creating or editing a product
                        this.clearEditableProduct()

                        this.savingProduct = false
                    } else {
                        this.$swal({
                            text: response.data.message,
                            type: 'warning'
                        })
                    }
                }).catch(error => {
                    this.savingProduct = false

                    if (error) {
                        this.$swal({
                            text: error.message,
                            type: 'error'
                        })
                    }
                })
            }
        },
        closeProductModal () {
            // if there are any changes to product after modal was opened, prompt user before closing
            if (this.productChanges()) {
                this.$swal({
                    html: 'Changes have been made to this product. If you proceed, all changes will be lost.',
                    showCancelButton: true,
                    confirmButtonText: 'Discard Changes',
                    confirmButtonClass: 'btn btn-danger',
                    cancelButtonText: 'Keep Editing',
                    cancelButtonClass: 'btn btn-light',
                    buttonsStyling: false,
                    reverseButtons: true
                }).then((willDiscardChanges) => {
                    if (willDiscardChanges.value) {
                        this.$bvModal.hide('product-modal')

                        this.clearEditableProduct()
                    }
                })
            } else {
                this.$bvModal.hide('product-modal')

                this.clearEditableProduct()
            }
        },
        validateProduct () {
            this.underValidation = true

            let valid = true

            /* check product properties on form submit. If they do not have a value, show validation
            messages and focus that field */

            if (!this.editableProduct.name) {
                valid = false
                this.$refs.productName.focus()
            }

            else if (!this.editableProduct.sku || this.skuExists) {
                valid = false
                this.$refs.productSKU.focus()
            }

            else if (!this.editableProduct.price) {
                valid = false
                this.$refs.productPrice.focus()
            }

            return valid
        },
        productChanges () {
            /* when creating or editing a product, the existing data is assigned to two separate objects so they can be compared
            against each other to determine if there were any changes made */
            let productChanges = false
            if (!_.isEqual(this.editableProduct, this.editableProductCache)) {
                productChanges = true
            }
            return productChanges
        },
        clearEditableProduct () {
            this.editableProduct = {
                statesgrades: []
            }
            this.editableProductCache = {
                statesgrades: []
            }
        },
        relate (product, state, grade) {
            if (!product.statesgrades) {
                product.statesgrades = []
            }

            let relatedProduct = _.find(product.statesgrades, (o) => {
                return _.isMatch(o, {state: state.state, grade: grade})
            })

            if (!_.includes(product.statesgrades, relatedProduct)) {
                product.statesgrades.push({
                    state: state.state,
                    grade: grade
                })
            } else {
                let index = _.findIndex(product.statesgrades, {
                    state: state.state, grade: grade
                })
                product.statesgrades.splice(index, 1)
            }
        },
        bulkRelateGrade (product, states, grade) {
            /*
                The bulk relate needs to only toggle states that have not already been
                related.
            */

            let productsMatchingGradeBeingRelated = 0

            if (!product.statesgrades) {
                product.statesgrades = []
            } else {
                _.filter(product.statesgrades, (o) => {
                    if (_.isMatch(o.grade, grade)) {
                        // count number of products matching the grade that is being related
                        productsMatchingGradeBeingRelated++
                    }
                })
            }

            _.forEach(states, state => {
                // find products that are already related
                let relatedProduct = _.find(product.statesgrades, (o) => {
                    return _.isMatch(o, {state: state.state, grade: grade})
                })

                if (!_.includes(product.statesgrades, relatedProduct)) {
                    // if product does not already exist in 'product.statesgrades', create it
                    product.statesgrades.push({
                        state: state.state,
                        grade: grade
                    })
                } else if (productsMatchingGradeBeingRelated == states.length) {
                    /*
                        if 'productsMatchingGradeBeingRelated' is the same number as 'states.length', we can assume the all states have been related,
                        in which case we want to find and splice the already related items
                    */
                    let index = _.findIndex(product.statesgrades, {
                        state: state.state, grade: grade
                    })
                    product.statesgrades.splice(index, 1)
                }
            })
        },
        bulkRelateState (product, state, grades) {
            /*
                similar to the 'bulkRelateGrade' method. see comments for explanations.
            */
            if (state.state) {

                let productsMatchingStateBeingRelated = 0

                if (!product.statesgrades) {
                    product.statesgrades = []
                } else {
                    _.filter(product.statesgrades, (o) => {
                        if (_.isMatch(o.state, state.state)) {
                            productsMatchingStateBeingRelated++
                        }
                    })
                }

                _.forEach(grades, grade => {
                    let relatedProduct = _.find(product.statesgrades, (o) => {
                        return _.isMatch(o, {state: state.state, grade: grade})
                    })

                    if (!_.includes(product.statesgrades, relatedProduct)) {
                        product.statesgrades.push({
                            state: state.state,
                            grade: grade
                        })
                    } else if (productsMatchingStateBeingRelated == grades.length) {
                        let index = _.findIndex(product.statesgrades, {state: state.state, grade: grade})
                        product.statesgrades.splice(index, 1)
                    }
                })
            }
        },
        isRelated (statesGrades, state, grade) {
            // checks if state/grade has already been related
            let isRelated = false

            if (statesGrades) {
                _.filter(statesGrades, (o) => {
                    if (
                       o.state == state.state &&
                       o.grade == grade
                   ) {
                       isRelated = true
                   }
                })
            }

            return isRelated
        },
        productModalOpened () {
            // when creating a new product, focus "name" input when modal is opened
            if (!this.editableProduct.id) {
                this.$refs.productName.focus()
            }
        },
        permission_error () {
            alert("Sorry, you don't have the right permissions to perform this action.")
        },
    },
    mounted () {
        // get products on component mount
        this.getProducts()
    }
}
</script>
